import React, { useMemo, useState } from "react";
import { compute } from "@/lib/calculations";
import { fmt$ } from "@/lib/format";

const PRESETS = ["Current Offer", "Lower Purchase Price", "More Seller Credit", "Lower Rate", "Minimum Cash Needed", "Custom"];

function applyPreset(base, preset) {
  const b = { ...base };
  switch (preset) {
    case "Lower Purchase Price":
      return { ...b, purchase_price: String(Math.max(0, Number(b.purchase_price || 0) - 25000)) };
    case "More Seller Credit":
      return { ...b, seller_credit: String(Math.max(Number(b.seller_credit || 0), 5000) + 2500) };
    case "Lower Rate":
      return { ...b, interest_rate: String(Math.max(0, Number(b.interest_rate || 0) - 0.5)) };
    case "Minimum Cash Needed":
      return { ...b, seller_credit: String(Math.max(Number(b.seller_credit || 0), 8000)), down_payment: 0, down_payment_is_pct: false };
    case "Custom":
    case "Current Offer":
    default:
      return b;
  }
}

export default function ScenarioLab({ baseInputs }) {
  const [scenarios, setScenarios] = useState([
    { id: "a", label: "Current Offer", inputs: { ...baseInputs } },
    { id: "b", label: "More Seller Credit", inputs: applyPreset(baseInputs, "More Seller Credit") },
    { id: "c", label: "Lower Rate", inputs: applyPreset(baseInputs, "Lower Rate") },
  ]);

  const setSc = (i, k, v) => setScenarios(prev => prev.map((s, idx) => idx === i ? { ...s, inputs: { ...s.inputs, [k]: v } } : s));
  const setLbl = (i, v) => setScenarios(prev => prev.map((s, idx) => idx === i ? { ...s, label: v, inputs: applyPreset(baseInputs, v) } : s));

  const results = useMemo(() => scenarios.map(s => compute(s.inputs)), [scenarios]);
  const baseResult = results[0];

  // A scenario shows "diminishing seller-credit return" when: (a) it has more
  // total credits than the base, AND (b) its cash-at-closing didn't improve
  // (both are $0, which is the only way credits can be fully absorbed).
  const diminishingReturn = (i) => {
    if (i === 0) return false;
    const r = results[i];
    const b = baseResult;
    if (!r || !b) return false;
    const moreCredit = (r.total_credits || 0) > (b.total_credits || 0) + 0.5;
    const sameCashAtClosing = Math.abs((r.additional_cash_at_closing || 0) - (b.additional_cash_at_closing || 0)) < 0.5;
    const baseAlreadyZero = (b.additional_cash_at_closing || 0) < 0.5;
    return moreCredit && sameCashAtClosing && baseAlreadyZero;
  };

  return (
    <div data-testid="scenario-lab">
      <div className="mb-5">
        <h4 className="font-display text-xl text-brand-ink">What if you changed your offer?</h4>
        <p className="text-sm text-brand-muted mt-1">Compare different purchase prices, seller credits, down payments, and interest rates to see how they affect both your monthly payment and the cash you may need.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {scenarios.map((s, i) => {
          const r = results[i];
          const base = baseInputs;
          const changed = (k) => String(s.inputs[k] ?? "") !== String(base[k] ?? "");
          const hasUnused = (r.unused_credit || 0) > 0.5;
          const showDiminishing = diminishingReturn(i);
          return (
            <div key={s.id} data-testid={`scenario-${i}`} className="rounded-2xl border border-brand-border bg-brand-bg/60 p-4">
              <select className="hs-input mb-3 text-sm" value={s.label} onChange={e => setLbl(i, e.target.value)}>
                {PRESETS.map(p => <option key={p}>{p}</option>)}
              </select>
              <div className="space-y-2">
                <SFld label="Purchase price" value={s.inputs.purchase_price} onChange={v => setSc(i, "purchase_price", v)} changed={changed("purchase_price")} />
                <SFld label="Interest rate" value={s.inputs.interest_rate} onChange={v => setSc(i, "interest_rate", v)} suffix="%" changed={changed("interest_rate")} />
                <SFld label="Down payment" value={s.inputs.down_payment} onChange={v => setSc(i, "down_payment", v)} changed={changed("down_payment")} />
                <SFld label="Seller credit" value={s.inputs.seller_credit} onChange={v => setSc(i, "seller_credit", v)} changed={changed("seller_credit")} />
                <SFld label="Discount points" value={s.inputs.discount_points} onChange={v => setSc(i, "discount_points", v)} changed={changed("discount_points")} />
              </div>
              <div className="mt-4 space-y-1.5 text-sm border-t border-brand-border pt-3">
                <Line k="Monthly" v={fmt$(r.monthly_total)} />
                <Line k="Cash before closing" v={fmt$(r.cash_before_closing)} />
                <Line k="Cash at closing" v={fmt$(r.additional_cash_at_closing)} />
                <Line k="Max transaction cash" v={fmt$(r.max_transaction_cash)} bold />
                {r.cash_remaining != null && (
                  r.cash_remaining < 0
                    ? <Line k="Cash shortfall" v={fmt$(Math.abs(r.cash_remaining))} shortfall />
                    : <Line k="Cash cushion" v={fmt$(r.cash_remaining)} />
                )}
              </div>
              {showDiminishing && (
                <div data-testid={`scenario-${i}-diminishing`} className="mt-3 rounded-xl border border-brand-warning/40 bg-brand-warning/10 p-3 text-xs leading-relaxed">
                  <div className="font-semibold text-brand-ink">No additional cash-to-close benefit</div>
                  <p className="text-brand-muted mt-0.5">Your current seller credit already covers the eligible costs due at closing. Additional seller credit can't be paid back to you as cash and doesn't reduce earnest money, inspection, or appraisal expenses.</p>
                  {hasUnused && <p className="text-brand-muted mt-1"><b className="text-brand-ink">Potential unused seller credit:</b> {fmt$(r.unused_credit)}</p>}
                </div>
              )}
              {!showDiminishing && hasUnused && (
                <div data-testid={`scenario-${i}-unused`} className="mt-3 rounded-xl border border-brand-border bg-brand-soft/40 p-3 text-xs text-brand-muted leading-relaxed">
                  <b className="text-brand-ink">Heads up:</b> about {fmt$(r.unused_credit)} of the entered credit exceeds eligible closing costs. Credits can't be returned as cash or applied to pre-closing expenses.
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SFld({ label, value, onChange, suffix, changed }) {
  return (
    <div>
      <label className="text-xs text-brand-muted flex items-center gap-1">
        {label}
        {changed && <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-accent" title="Changed from base" />}
      </label>
      <div className="relative">
        <input className={`hs-input py-2 text-sm ${changed ? "border-brand-accent" : ""}`} value={value ?? ""} onChange={e => onChange(e.target.value)} />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted text-xs">{suffix}</span>}
      </div>
    </div>
  );
}
function Line({ k, v, bold, shortfall }) {
  return <div className="flex justify-between"><span className="text-brand-muted">{k}</span><span className={shortfall ? "font-semibold text-brand-danger" : bold ? "font-semibold text-brand-accent" : "text-brand-ink"}>{v}</span></div>;
}
